<template>
  <main class="guests-page">
    <div class="guests-page__head">
      <h1 class="guests-page__title">Contabilità</h1>
    </div>

    <p class="guests-page__subtitle" style="margin: -6px 0 16px">
      Documenti retta, ricevute/proforme e note di credito di tutte le
      strutture. Documento gestionale: non è fattura elettronica.
    </p>

    <!-- Tab interne della sezione Contabilità -->
    <div class="facility-tabs">
      <button
        v-for="t in tabs"
        :key="t.key"
        class="facility-tab"
        :class="{ 'facility-tab--active': activeTab === t.key }"
        @click="activeTab = t.key"
      >
        <i :class="t.icon"></i>
        <span>{{ t.label }}</span>
      </button>
    </div>

    <!-- ================= ARCHIVIO DOCUMENTI ================= -->
    <section v-show="activeTab === 'archive'">
      <div class="guest-detail" style="margin-bottom: 14px">
        <div class="billing-filters">
          <select v-model="filters.facilityId" class="search-box">
            <option value="">Tutte le strutture</option>
            <option v-for="f in facilities" :key="f.id" :value="f.id">
              {{ f.name }}
            </option>
          </select>
          <select v-model="filters.type" class="search-box">
            <option value="">Tutti i tipi</option>
            <option value="monthly_fee">Documento retta</option>
            <option value="credit_note">Nota di credito</option>
          </select>
          <select v-model="filters.status" class="search-box">
            <option value="">Tutti gli stati</option>
            <option value="generated">Emesso</option>
            <option value="partially_credited">Stornato parz.</option>
            <option value="credited">Stornato</option>
            <option value="cancelled">Annullato</option>
          </select>
          <select v-model.number="filters.month" class="search-box">
            <option :value="0">Tutti i mesi</option>
            <option v-for="(m, i) in monthNames" :key="i" :value="i + 1">
              {{ m }}
            </option>
          </select>
          <select v-model.number="filters.year" class="search-box">
            <option :value="0">Tutti gli anni</option>
            <option v-for="y in yearOptions" :key="y" :value="y">
              {{ y }}
            </option>
          </select>
          <input
            v-model="filters.documentNumber"
            class="search-box"
            placeholder="N° documento"
          />
          <input
            v-model="guestQuery"
            class="search-box"
            placeholder="Ospite (filtro)"
          />
          <button
            class="villa-btn villa-btn--ghost"
            :disabled="loading"
            @click="loadArchive"
          >
            <i
              :class="
                loading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'
              "
            ></i>
            Aggiorna
          </button>
        </div>
      </div>

      <div class="guests-table-wrap">
        <table class="guests-table">
          <thead>
            <tr>
              <th>Numero</th>
              <th>Tipo</th>
              <th>Struttura</th>
              <th>Ospite</th>
              <th>Competenza</th>
              <th>Data</th>
              <th>Importo</th>
              <th>Stato</th>
              <th class="actions-cell">Azioni</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!filteredRows.length" class="no-result-row">
              <td colspan="9" class="no-result">Nessun documento trovato.</td>
            </tr>
            <tr v-for="r in filteredRows" :key="r.id">
              <td>{{ r.documentNumber }}</td>
              <td>{{ typeLabel(r.documentType) }}</td>
              <td>{{ r.facilityName || "—" }}</td>
              <td>{{ r.guestName || "—" }}</td>
              <td>{{ competence(r) }}</td>
              <td>{{ fmtDate(r.documentDate) }}</td>
              <td>{{ euro(r.amount) }}</td>
              <td>
                <span :class="['status-badge', statusClass(r.status)]">
                  {{ statusLabel(r.status) }}
                </span>
              </td>
              <td class="actions-cell">
                <button
                  class="icon-btn"
                  title="Stampa"
                  :disabled="!r.hasFile || printingId === r.id"
                  @click="printDoc(r)"
                >
                  <i
                    :class="
                      printingId === r.id
                        ? 'fa-solid fa-spinner fa-spin'
                        : 'fa-solid fa-print'
                    "
                  ></i>
                </button>
                <button
                  class="icon-btn"
                  title="Scarica PDF"
                  :disabled="!r.hasFile"
                  @click="downloadDoc(r)"
                >
                  <i class="fa-solid fa-download"></i>
                </button>
                <button
                  v-if="canCredit(r)"
                  class="icon-btn"
                  title="Genera nota di credito"
                  @click="openCreditNote(r)"
                >
                  <i class="fa-solid fa-file-circle-minus"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- ================= GENERA DOCUMENTI ================= -->
    <section v-show="activeTab === 'generate'">
      <div class="guest-detail" style="margin-bottom: 14px">
        <label class="form-field" style="max-width: 360px">
          <span style="font-size: 13px; font-weight: 600; color: #334155">
            Struttura
          </span>
          <select v-model="genFacilityId" class="search-box">
            <option value="">Seleziona una struttura…</option>
            <option v-for="f in facilities" :key="f.id" :value="f.id">
              {{ f.name }}
            </option>
          </select>
        </label>
      </div>
      <FacilityBillingPanel
        v-if="genFacilityId"
        :key="genFacilityId"
        :facility-id="genFacilityId"
        @changed="onGenerated"
      />
      <div v-else class="guests-loading">
        Seleziona una struttura per generare i documenti retta in blocco.
      </div>
    </section>

    <!-- ================= PROGRESSIVI ================= -->
    <section v-show="activeTab === 'sequences'">
      <div class="guests-table-wrap" style="margin-bottom: 18px">
        <table class="guests-table">
          <thead>
            <tr>
              <th>Struttura</th>
              <th>Tipo</th>
              <th>Anno</th>
              <th>Codice</th>
              <th>Prossimo numero</th>
              <th>Anteprima</th>
              <th class="actions-cell">Modifica</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!sequences.length" class="no-result-row">
              <td colspan="7" class="no-result">
                Nessuna sequenza: si crea automaticamente alla prima
                generazione.
              </td>
            </tr>
            <tr v-for="s in sequences" :key="s.id">
              <td>{{ s.facilityName || "—" }}</td>
              <td>{{ typeLabel(s.documentType) }}</td>
              <td>{{ s.year }}</td>
              <td>{{ s.facilityCode || "—" }}</td>
              <td>
                <input
                  v-model.number="seqEdits[s.id]"
                  type="number"
                  min="1"
                  class="search-box"
                  style="min-width: 90px"
                />
              </td>
              <td>{{ previewFor(s) }}</td>
              <td class="actions-cell">
                <button
                  class="villa-btn villa-btn--ghost"
                  :disabled="
                    savingSeq === s.id || seqEdits[s.id] === s.nextNumber
                  "
                  @click="saveSequence(s)"
                >
                  <i
                    :class="
                      savingSeq === s.id
                        ? 'fa-solid fa-spinner fa-spin'
                        : 'fa-solid fa-floppy-disk'
                    "
                  ></i>
                  Salva
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="guest-detail">
        <h3 class="detail-block-title">Nuova sequenza</h3>
        <div class="billing-filters">
          <select v-model="newSeq.facilityId" class="search-box">
            <option value="">Struttura…</option>
            <option v-for="f in facilities" :key="f.id" :value="f.id">
              {{ f.name }}
            </option>
          </select>
          <select v-model="newSeq.documentType" class="search-box">
            <option value="monthly_fee">Documento retta</option>
            <option value="credit_note">Nota di credito</option>
          </select>
          <select v-model.number="newSeq.year" class="search-box">
            <option v-for="y in yearOptions" :key="y" :value="y">
              {{ y }}
            </option>
          </select>
          <input
            v-model.number="newSeq.startNumber"
            type="number"
            min="1"
            class="search-box"
            placeholder="Numero di partenza"
            style="min-width: 150px"
          />
          <button
            class="villa-btn villa-btn--primary"
            :disabled="!newSeq.facilityId || creatingSeq"
            @click="createSeq"
          >
            <i
              :class="
                creatingSeq ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-plus'
              "
            ></i>
            Crea sequenza
          </button>
        </div>
        <p class="guests-page__subtitle" style="margin-top: 8px">
          Permette di partire da un numero già avanzato (es. 53). Il sistema
          impedisce di riusare numeri già assegnati.
        </p>
      </div>
    </section>

    <!-- ================= MODALE NOTA DI CREDITO ================= -->
    <div
      v-if="creditModal.open"
      class="villa-modal-backdrop"
      @click.self="closeCreditNote"
    >
      <div class="villa-confirm" style="max-width: 460px">
        <h3 class="villa-confirm__title">Nota di credito</h3>
        <p class="villa-confirm__text" style="margin-bottom: 14px">
          Storno del documento
          <strong>{{
            creditModal.row && creditModal.row.documentNumber
          }}</strong>
          — {{ creditModal.row && creditModal.row.guestName }}.
        </p>
        <div class="form-field" style="margin-bottom: 12px">
          <label>Importo da stornare (€)</label>
          <input
            v-model.number="creditModal.amount"
            type="number"
            min="0.01"
            step="0.01"
            class="search-box"
          />
        </div>
        <div class="form-field" style="margin-bottom: 16px">
          <label>Causale</label>
          <textarea
            v-model="creditModal.reason"
            rows="3"
            class="search-box"
            placeholder="Es. Storno parziale retta per assenza"
          ></textarea>
        </div>
        <div
          v-if="bankAccounts.length"
          class="form-field"
          style="margin-bottom: 16px"
        >
          <label>Conto bancario (rimborso)</label>
          <select v-model.number="creditModal.bankAccountId" class="search-box">
            <option v-for="a in bankAccounts" :key="a.id" :value="a.id">
              {{ a.label }}{{ a.isDefault ? " (default)" : "" }}
            </option>
          </select>
        </div>
        <div class="villa-confirm__actions">
          <button class="villa-btn villa-btn--ghost" @click="closeCreditNote">
            Annulla
          </button>
          <button
            class="villa-btn villa-btn--primary"
            :disabled="creditModal.saving"
            @click="confirmCreditNote"
          >
            <i
              :class="
                creditModal.saving
                  ? 'fa-solid fa-spinner fa-spin'
                  : 'fa-solid fa-file-circle-minus'
              "
            ></i>
            Genera nota di credito
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapActions } from "vuex";
import FacilityBillingPanel from "@/components/FacilityBillingPanel.vue";
import {
  printPdfFromResponse,
  isPopupBlocked,
  POPUP_BLOCKED_MESSAGE,
} from "@/utils/printPdf";

const MONTH_NAMES = [
  "Gennaio",
  "Febbraio",
  "Marzo",
  "Aprile",
  "Maggio",
  "Giugno",
  "Luglio",
  "Agosto",
  "Settembre",
  "Ottobre",
  "Novembre",
  "Dicembre",
];

export default {
  name: "BillingPage",
  components: { FacilityBillingPanel },
  data() {
    const now = new Date();
    return {
      activeTab: "archive",
      monthNames: MONTH_NAMES,
      tabs: [
        {
          key: "archive",
          label: "Archivio documenti",
          icon: "fa-solid fa-folder-open",
        },
        {
          key: "generate",
          label: "Genera documenti",
          icon: "fa-solid fa-file-circle-plus",
        },
        { key: "sequences", label: "Progressivi", icon: "fa-solid fa-hashtag" },
      ],
      facilities: [],
      bankAccounts: [],
      // Archivio
      rows: [],
      loading: false,
      printingId: null,
      filters: {
        facilityId: "",
        type: "",
        status: "",
        month: 0,
        year: 0,
        documentNumber: "",
      },
      guestQuery: "",
      // Genera
      genFacilityId: "",
      // Progressivi
      sequences: [],
      seqEdits: {},
      savingSeq: null,
      creatingSeq: false,
      newSeq: {
        facilityId: "",
        documentType: "monthly_fee",
        year: now.getFullYear(),
        startNumber: 1,
      },
      // Nota di credito
      creditModal: {
        open: false,
        row: null,
        amount: null,
        reason: "",
        saving: false,
        bankAccountId: null,
      },
    };
  },
  computed: {
    yearOptions() {
      const y = new Date().getFullYear();
      return [y - 2, y - 1, y, y + 1];
    },
    filteredRows() {
      const q = this.guestQuery.trim().toLowerCase();
      if (!q) return this.rows;
      return this.rows.filter((r) =>
        (r.guestName || "").toLowerCase().includes(q)
      );
    },
  },
  methods: {
    ...mapActions([
      "fetchFacilities",
      "fetchBillingDocuments",
      "downloadBillingDocument",
      "createBillingCreditNote",
      "fetchBillingSequences",
      "updateBillingSequence",
      "createBillingSequence",
      "fetchBankAccounts",
    ]),
    async loadBankAccounts() {
      try {
        this.bankAccounts = await this.fetchBankAccounts({ activeOnly: true });
      } catch {
        this.bankAccounts = [];
      }
    },
    defaultBankAccountId() {
      const def =
        this.bankAccounts.find((a) => a.isDefault) || this.bankAccounts[0];
      return def ? def.id : null;
    },
    euro(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    fmtDate(d) {
      if (!d) return "—";
      const s = String(d).slice(0, 10).split("-");
      return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : String(d);
    },
    typeLabel(t) {
      return t === "credit_note" ? "Nota di credito" : "Documento retta";
    },
    statusLabel(s) {
      return (
        {
          generated: "Emesso",
          partially_credited: "Stornato parz.",
          credited: "Stornato",
          cancelled: "Annullato",
        }[s] || s
      );
    },
    statusClass(s) {
      return (
        {
          generated: "status-badge--active",
          partially_credited: "status-badge--suspended",
          credited: "status-badge--discharged",
          cancelled: "status-badge--deceased",
        }[s] || ""
      );
    },
    competence(r) {
      if (!r.billingMonth || !r.billingYear) return "—";
      return `${MONTH_NAMES[r.billingMonth - 1]} ${r.billingYear}`;
    },
    canCredit(r) {
      return (
        r.documentType === "monthly_fee" &&
        r.status !== "credited" &&
        r.status !== "cancelled"
      );
    },
    previewFor(s) {
      const next = this.seqEdits[s.id] ?? s.nextNumber;
      const n = String(next || 0).padStart(s.padding || 3, "0");
      const code = s.facilityCode || "—";
      return s.documentType === "credit_note"
        ? `NC-${n}/${s.year}/${code}`
        : `${n}/${s.year}/${code}`;
    },
    async loadArchive() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.facilityId)
          params.facilityId = this.filters.facilityId;
        if (this.filters.type) params.type = this.filters.type;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.month) params.month = this.filters.month;
        if (this.filters.year) params.year = this.filters.year;
        if (this.filters.documentNumber)
          params.documentNumber = this.filters.documentNumber;
        this.rows = await this.fetchBillingDocuments(params);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Caricamento archivio non riuscito."
        );
      } finally {
        this.loading = false;
      }
    },
    async loadSequences() {
      try {
        this.sequences = await this.fetchBillingSequences();
        const edits = {};
        this.sequences.forEach((s) => {
          edits[s.id] = s.nextNumber;
        });
        this.seqEdits = edits;
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Caricamento progressivi non riuscito."
        );
      }
    },
    async printDoc(r) {
      // Riusa l'endpoint di download esistente: nessuna rigenerazione, nessun
      // nuovo record, nessun cambio di progressivo/stato.
      this.printingId = r.id;
      try {
        const res = await this.downloadBillingDocument(r.id);
        printPdfFromResponse(res);
      } catch (err) {
        this.$toast.error(
          isPopupBlocked(err)
            ? POPUP_BLOCKED_MESSAGE
            : err?.response?.data?.message || "Stampa non riuscita."
        );
      } finally {
        this.printingId = null;
      }
    },
    async downloadDoc(r) {
      try {
        const res = await this.downloadBillingDocument(r.id);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${r.documentNumber.replace(/\//g, "-")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        this.$toast.error("Download non riuscito.");
      }
    },
    openCreditNote(r) {
      this.creditModal = {
        open: true,
        row: r,
        amount: Number(r.amount),
        reason: "",
        saving: false,
        bankAccountId: this.defaultBankAccountId(),
      };
    },
    closeCreditNote() {
      this.creditModal.open = false;
    },
    async confirmCreditNote() {
      const { row, amount, reason, bankAccountId } = this.creditModal;
      this.creditModal.saving = true;
      try {
        await this.createBillingCreditNote({
          id: row.id,
          amount,
          reason,
          bankAccountId: bankAccountId || undefined,
        });
        this.$toast.success("Nota di credito generata.");
        this.creditModal.open = false;
        await this.loadArchive();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Generazione nota di credito non riuscita."
        );
      } finally {
        this.creditModal.saving = false;
      }
    },
    async saveSequence(s) {
      this.savingSeq = s.id;
      try {
        const updated = await this.updateBillingSequence({
          id: s.id,
          nextNumber: this.seqEdits[s.id],
        });
        const i = this.sequences.findIndex((x) => x.id === s.id);
        if (i !== -1) this.sequences.splice(i, 1, updated);
        this.$toast.success("Progressivo aggiornato.");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Aggiornamento progressivo non riuscito."
        );
        this.seqEdits[s.id] = s.nextNumber;
      } finally {
        this.savingSeq = null;
      }
    },
    async createSeq() {
      this.creatingSeq = true;
      try {
        await this.createBillingSequence({ ...this.newSeq });
        this.$toast.success("Sequenza creata.");
        this.newSeq.startNumber = 1;
        await this.loadSequences();
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message || "Creazione sequenza non riuscita."
        );
      } finally {
        this.creatingSeq = false;
      }
    },
    onGenerated() {
      // Dopo una generazione bulk, mantieni archivio/progressivi aggiornabili.
      this.loadSequences();
    },
  },
  async mounted() {
    this.facilities = await this.fetchFacilities();
    await Promise.all([
      this.loadArchive(),
      this.loadSequences(),
      this.loadBankAccounts(),
    ]);
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/AppLayout.css" />
<style scoped>
.billing-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.billing-filters .search-box {
  min-width: 150px;
}
.villa-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(8, 43, 87, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}
</style>
